import { AccidentInsuranceRoutes } from "components/accidentInsurance/AccidentInsuranceRoutes";
import { BankCardRoutes } from "components/bankCard/BankCardRoutes";
import { BSPOptionsRoutes } from "components/BSPOptions/BSPOptionsRoutes";
import { CarLeasingRoutes } from "components/carLeasing/CarLeasingRoutes";
import { CreditCardUpgradeRoutes } from "components/creditCardUpgrade/CreditCardUpgradeRoutes";
import { JuniorRoutes } from "components/gJunior/JuniorRoutes";
import { HouseholdInsuranceRoutes } from "components/householdInsurance/HouseholdInsuranceRoutes";
import { LifeInsuranceRoutes } from "components/lifeInsurance/LifeInsuranceRoutes";
import { ConsumerLoanRoutes } from "components/loan/ConsumerLoanRoutes";
import { OptioPayRoutes } from "components/optiopay/OptioPayRoutes";
import { OptioPayActivationRoutes } from "components/optiopayActivation/OptioPayActivationRoutes";
import { PensionFundsRoutes } from "components/pensionFunds/PensionFundsRoutes";
import { PensionInsuranceRoutes } from "components/pensionInsurance/PensionInsuranceRoutes";
import { SavingsOptionsRoutes } from "components/savingsOptions/SavingsOptionsRoutes";
import { ShopCreditCardRoutes } from "components/shopCreditCard/ShopCreditCardRoutes";
import { StoreRoutes } from "components/store/StoreRoutes";
import { SurveyRoutes } from "components/survey/SurveyRoutes";
import { observer } from "mobx-react";
import * as React from "react";
import { matchPath, useHistory, useLocation } from "react-router";
import externalInterfaceStore from "stores/ExternalInterfaceStore";
import profileStore from "stores/ProfileStore";
import trackingStore from "stores/TrackingStore";

type ITrackingConfig = {
    [rootUrl: string]: {
        productKey?: "productCode" | "channelProduct";
        productKeyFallback?: string;
        names: {
            [url: string]: string
        }
    }
};

const trackingConfig: ITrackingConfig = {
    [StoreRoutes.ROOT]: {
        names: {
            [StoreRoutes.ROOT]: "storeOverview"
        }
    },
    [OptioPayActivationRoutes.ROOT]: {
        productKey: "productCode",
        productKeyFallback: "PI_VOUCHER",
        names: {
            [OptioPayActivationRoutes.ROOT]: "landingPage",
            [OptioPayActivationRoutes.ACTIVATION]: "Activation",
            [OptioPayActivationRoutes.SIGNING]: "Activation/Signing",
            [OptioPayActivationRoutes.SUCCESS]: "Activation/Signing/Success",
        }
    },
    [OptioPayRoutes.ROOT]: {
        productKey: "productCode",
        productKeyFallback: "PI_VOUCHER",
        names: {
            [OptioPayRoutes.ROOT]: "Overview",
            [OptioPayRoutes.FAQ]: "Overview/VouchersFaqs",
            [OptioPayRoutes.CODE]: "Overview/ShowVoucherCodePin",
            [OptioPayRoutes.DEACTIVATION]: "Deactivation",
            [OptioPayRoutes.SIGNING]: "Deactivation/Signing",
            [OptioPayRoutes.SUCCESS]: "Deactivation/Signing/Success",
        }
    },
    [ShopCreditCardRoutes.ROOT]: {
        productKey: "channelProduct",
        names: {
            [ShopCreditCardRoutes.ROOT]: "LandingPage",
            [ShopCreditCardRoutes.PERSONAL_DATA]: "PersonalData",
            [ShopCreditCardRoutes.CARD_INFO]: "CardData",
            [ShopCreditCardRoutes.SUMMARY]: "Summary",
            [ShopCreditCardRoutes.SIGNING]: "Signing",
            [ShopCreditCardRoutes.SUCCESS]: "Signing/Success",
        }
    },
    [BankCardRoutes.ROOT]: {
        productKey: "channelProduct",
        names: {
            [BankCardRoutes.ROOT]: "LandingPage",
            [BankCardRoutes.PERSONAL_DATA]: "PersonalData",
            [BankCardRoutes.SUMMARY]: "Summary",
            [BankCardRoutes.SIGNING]: "Signing",
            [BankCardRoutes.SUCCESS]: "Signing/Success",
        }
    },
    [AccidentInsuranceRoutes.ROOT]: {
        productKey: "channelProduct",
        names: {
            [AccidentInsuranceRoutes.ROOT]: "LandingPage",
            [AccidentInsuranceRoutes.PERSONAL_DATA]: "PersonalData",
            [AccidentInsuranceRoutes.SUCCESS]: "Success",
        }
    },
    [LifeInsuranceRoutes.ROOT]: {
        productKey: "channelProduct",
        names: {
            [LifeInsuranceRoutes.ROOT]: "LandingPage",
            [LifeInsuranceRoutes.PERSONAL_DATA]: "PersonalData",
            [LifeInsuranceRoutes.SUCCESS]: "Success",
        }
    },
    [PensionFundsRoutes.ROOT]: {
        productKey: "channelProduct",
        productKeyFallback: "PENSION_FUNDS_MANAGE",
        names: {
            [PensionFundsRoutes.ROOT]: "LandingPage",
            [PensionFundsRoutes.PERSONAL_DATA]: "PersonalData",
            [PensionFundsRoutes.SUCCESS]: "Success",
        }
    },
    [PensionInsuranceRoutes.ROOT]: {
        productKey: "channelProduct",
        productKeyFallback: "PENSION_INSURANCE",
        names: {
            [PensionInsuranceRoutes.ROOT]: "LandingPage",
            [PensionInsuranceRoutes.PERSONAL_DATA]: "PersonalData",
            [PensionInsuranceRoutes.SUCCESS]: "Success",
        }
    },
    [CarLeasingRoutes.ROOT]: {
        productKey: "channelProduct",
        productKeyFallback: "CAR_LEASING",
        names: {
            [CarLeasingRoutes.ROOT]: "LandingPage",
        }
    },
    [ConsumerLoanRoutes.ROOT]: {
        productKey: "channelProduct",
        names: {
            [ConsumerLoanRoutes.ROOT]: "LandingPage",
            [ConsumerLoanRoutes.CALCULATOR]: "LoanData",
            [ConsumerLoanRoutes.ACCOUNT_SELECTION]: "AccountData",
            [ConsumerLoanRoutes.SUMMARY]: "Summary",
            [ConsumerLoanRoutes.SIGNING]: "Signing",
            [ConsumerLoanRoutes.REJECTED]: "Rejection",
            [ConsumerLoanRoutes.SUCCESS]: "Signing/Success",
        }
    },
    [CreditCardUpgradeRoutes.ROOT]: {
        productKey: "channelProduct",
        productKeyFallback: "CC_UPGRADE",
        names: {
            [CreditCardUpgradeRoutes.ROOT]: "LandingPage",
            [CreditCardUpgradeRoutes.PERSONAL_DATA]: "PersonalData",
            [CreditCardUpgradeRoutes.SUMMARY]: "Summary",
            [CreditCardUpgradeRoutes.SIGNING]: "Signing",
            [CreditCardUpgradeRoutes.SUCCESS]: "Signing/Success",
            [CreditCardUpgradeRoutes.ERROR_UPGRADE_NOT_POSSIBLE]: "UpgradeUnavailable",
        }
    },
    [BSPOptionsRoutes.ROOT]: {
        productKey: "channelProduct",
        productKeyFallback: "B-SPA-*",
        names: {
            [BSPOptionsRoutes.ROOT]: "Overview",
            [BSPOptionsRoutes.DETAILS]: "Details",
            [BSPOptionsRoutes.SUMMARY]: "Summary",
            [BSPOptionsRoutes.SIGNING]: "Signing",
            [BSPOptionsRoutes.SUCCESS]: "Signing/Success",
        }
    },
    [SurveyRoutes.ROOT]: {
        names: {
            [SurveyRoutes.ROOT]: "kitPage",
            [SurveyRoutes.SIGNING]: "kitSigning",
        }
    },
    [SavingsOptionsRoutes.ROOT]: {
        productKey: "channelProduct",
        productKeyFallback: "SAVINGS_OPTIONS",
        names: {
            [SavingsOptionsRoutes.ROOT]: "Overview",
            [SavingsOptionsRoutes.SIGNING]: "Signing",
            [SavingsOptionsRoutes.SUCCESS]: "Success",
        }
    },
    [HouseholdInsuranceRoutes.ROOT]: {
        productKey: "channelProduct",
        productKeyFallback: " V-SAC-EHT",
        names: {
            [HouseholdInsuranceRoutes.ROOT]: "LandingPage",
            [HouseholdInsuranceRoutes.PERSONAL_DATA]: "PersonalData",
        }
    },
    [JuniorRoutes.ROOT]: {
        names: {
            [JuniorRoutes.ROOT]: "Overview",
            [JuniorRoutes.OVERVIEW]: "Overview",
            [JuniorRoutes.DETAIL]: "Detail",
            [JuniorRoutes.ACTIVATE_TOUR]: "ActivateJuniorUser_Tour",
            [JuniorRoutes.ACTIVATE_CONTACT]: "ActivateJuniorUser_Contact",
            [JuniorRoutes.ACTIVATE_DOCUMENTS]: "ActivateJuniorUser_Documents",
            [JuniorRoutes.ACTIVATE_SIGNING]: "ActivateJuniorUser_Signing",
            [JuniorRoutes.ACTIVATE_SUCCESS]: "ActivateJuniorUser_Success",
            [JuniorRoutes.ACTIVATE_ERROR]: "ActivateJuniorUser_Error",
            [JuniorRoutes.PDF]: "ViewPDFDocument",
        }
    }
};

export const RoutingManager = observer((props: { children: React.ReactNode }) => {
    const location = useLocation();
    const history = useHistory();

    React.useEffect(() => {
        // https://github.com/ReactTraining/react-router/issues/2144#issuecomment-150939358
        history.listen(() => {

            // Use setTimeout to make sure this runs after React Router's own listener.
            setTimeout(() => {
                window.scrollTo(0, 0);
            });
        });
    }, []);

    // HYB-47 Page Tracking
    React.useEffect(() => {
        const trackPage = async () => {
            const pathItems = location.pathname.split("/").filter(pathItem => !!pathItem);

            if (pathItems.length > 0) {
                const featureName = pathItems[0];
                const currentFeature = `/${featureName}`;
                const trackedFeatures = Object.keys(trackingConfig);

                for (let i = 0; i < trackedFeatures.length; i++) {
                    const featureKey = trackedFeatures[i];

                    if (currentFeature === featureKey) {
                        const featureConfig = trackingConfig[featureKey];

                        const trackingPath = Object.keys(featureConfig.names).find(path => matchPath(location.pathname, { path, exact: true }));

                        if (trackingPath) {
                            const trackingName = featureConfig.names[trackingPath];

                            trackingStore.productKey = featureConfig.productKey && externalInterfaceStore[featureConfig.productKey]
                                ? externalInterfaceStore[featureConfig.productKey]
                                : featureConfig.productKeyFallback;
                            trackingStore.pageName = trackingName;
                            trackingStore.feature = featureName;

                            await trackingStore.trackPage();
                        }

                        break;
                    }
                }
            }
        };

        trackingStore.wipe();

        if (profileStore.profile) {
            trackPage();
        }
    }, [location.pathname, profileStore.profile]);

    return <>{props.children}</>;
});



// WEBPACK FOOTER //
// ./src/components/app/RoutingManager.tsx